import type { DashboardSharingType } from '@risksmart-app/domain/src/types/consts/dashboard-sharing-type';
import type {
  InputMaybe,
  Scalars,
  UpdateDashboardMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

export interface UpdateDashboardTRPCInput {
  Id: string;
  Name: string;
  Description?: InputMaybe<string>;
  Sharing: DashboardSharingType;
  Content?: InputMaybe<Scalars['jsonb']['input']>;
  ContributorUserIds: string[];
  ContributorGroupIds: string[];
}

/**
 * Maps TRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): UpdateDashboardMutation => {
  return {
    updateChildDashboard: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useUpdateDashboardTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.dashboard.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.dashboard.dashboards.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.dashboard.dashboardById.queryKey(),
        });
      },
    }),
  });

  return {
    updateDashboard: async (
      variables: UpdateDashboardTRPCInput
    ): Promise<UpdateDashboardMutation> => {
      const result = await mutation.mutateAsync(variables);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
